import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import { useAuth } from "../../hooks/useAuth";
import api from "../../services/api";
import styles from "./TasksPage.module.css";

const RISK_LABELS = {
  sla_breach: "SLA breached",
  sla_warning: "SLA at risk",
  refill_waiting: "Refill awaiting review",
  refund_failed: "Refund failed",
  order_denied: "Order denied",
};

const STATUS_OPTIONS = ["open", "in_progress", "waiting", "resolved", "closed"];
const PRIORITY_OPTIONS = ["low", "normal", "medium", "high", "urgent"];

const METRIC_COLUMNS = [
  { key: "openCount", label: "Open" },
  { key: "inProgressCount", label: "In Progress" },
  { key: "closedCount", label: "Closed" },
  { key: "closedToday", label: "Closed Today" },
  { key: "closedWeek", label: "Closed Week" },
  { key: "closedMonth", label: "Closed Month" },
  { key: "avgFirstResponseMinutes", label: "Avg Response", format: "minutes" },
  { key: "avgResolutionMinutes", label: "Avg Resolution", format: "minutes" },
  { key: "repliesSent", label: "Replies" },
  { key: "interactionsHandled", label: "Interactions" },
  { key: "escalations", label: "Escalations" },
  { key: "reopened", label: "Reopened" },
  { key: "highPriorityHandled", label: "High Priority" },
  { key: "activeWorkload", label: "Workload" },
  { key: "lastActiveAt", label: "Last Active", format: "date" },
];

const formatValue = (value) =>
  value ? value.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) : "";

const formatMinutes = (mins) => {
  if (mins === null || mins === undefined) return "\u2014";
  if (mins < 60) return `${mins}m`;
  if (mins < 1440) return `${Math.floor(mins / 60)}h ${mins % 60}m`;
  return `${Math.floor(mins / 1440)}d ${Math.floor((mins % 1440) / 60)}h`;
};

const formatCell = (user, column) => {
  const value = user[column.key];
  if (column.format === "minutes") return formatMinutes(value);
  if (column.format === "date")
    return value ? new Date(value).toLocaleString() : "\u2014";
  return value ?? 0;
};

const INITIAL_FILTERS = {
  userId: "",
  role: "",
  from: "",
  to: "",
  status: "",
  priority: "",
  category: "",
};

const TasksTeamView = ({ risks, risksLoading, risksError, onRetryRisks }) => {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [sort, setSort] = useState({ key: null, dir: "desc" });
  const [options, setOptions] = useState({ users: [], roles: [], categories: [] });

  const loadTeam = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      ["userId", "from", "to", "status", "priority", "category"].forEach(
        (key) => filters[key] && params.append(key, filters[key])
      );
      const query = params.toString();
      const response = await api.get(
        `/admin/tasks/team-dashboard${query ? `?${query}` : ""}`
      );
      setData(response);
      setError(null);
      // Capture filter options from the first (unfiltered) response only
      setOptions((prev) =>
        prev.users.length > 0
          ? prev
          : {
              users: (response.users || []).map(({ userId, displayName }) => ({
                value: userId,
                label: displayName,
              })),
              roles: [
                ...new Set((response.users || []).map((u) => u.role).filter(Boolean)),
              ],
              categories: Object.keys(response.overview?.byCategory || {}),
            }
      );
    } catch (err) {
      console.error("Failed to load team dashboard:", err);
      setError("Failed to load team dashboard");
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const overview = data?.overview;

  const kpis = overview
    ? [
        { label: "Open", value: overview.totals?.open ?? 0 },
        { label: "In Progress", value: overview.totals?.inProgress ?? 0 },
        { label: "Closed", value: overview.totals?.closed ?? 0 },
        { label: "Avg Response", value: formatMinutes(overview.avgFirstResponseMinutes) },
        { label: "Avg Resolution", value: formatMinutes(overview.avgResolutionMinutes) },
        { label: "Reopened Rate", value: `${overview.reopenedRate ?? 0}%` },
        { label: "SLA Breaches", value: overview.slaBreachCount ?? 0, alert: true },
      ]
    : [];

  const selectFilters = [
    { key: "userId", placeholder: "All Users", options: options.users },
    { key: "role", placeholder: "All Roles", options: options.roles },
    { key: "status", placeholder: "All Statuses", options: STATUS_OPTIONS },
    { key: "priority", placeholder: "All Priorities", options: PRIORITY_OPTIONS },
    { key: "category", placeholder: "All Categories", options: options.categories },
  ];

  const sortedUsers = useMemo(() => {
    const list = filters.role
      ? (data?.users || []).filter((u) => u.role === filters.role)
      : data?.users || [];
    if (!sort.key) return list;
    return [...list].sort((a, b) => {
      const av = a[sort.key] ?? -1;
      const bv = b[sort.key] ?? -1;
      const cmp =
        typeof av === "string" || typeof bv === "string"
          ? String(av).localeCompare(String(bv))
          : av - bv;
      return sort.dir === "asc" ? cmp : -cmp;
    });
  }, [data, filters.role, sort]);

  const maxWorkload = Math.max(1, ...sortedUsers.map((u) => u.activeWorkload || 0));

  const handleSort = (key) =>
    setSort((prev) =>
      prev.key === key
        ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
        : { key, dir: "desc" }
    );

  const handleFilter = (key) => (e) =>
    setFilters((prev) => ({ ...prev, [key]: e.target.value }));

  const openUserTickets = (userId) =>
    navigate(`/admin/support-tickets?assignedTo=${encodeURIComponent(userId)}`);

  const sortArrow = (key) =>
    sort.key === key ? (sort.dir === "asc" ? " \u25B2" : " \u25BC") : "";

  const renderRetryError = (message, retry) => (
    <div className={styles.errorBox}>
      {message}{" "}
      <button className={styles.retryBtn} onClick={retry}>
        Retry
      </button>
    </div>
  );

  return (
    <div className={styles.teamView}>
      <div className={styles.teamHeader}>
        <h2 className={styles.sectionTitle}>Team Dashboard</h2>
        <span className={styles.teamBadge}>Super Admin</span>
      </div>

      {/* Urgent Issues */}
      {hasPermission("tasks_urgent_issues.view") && <div className={styles.urgentPanel}>
        <div className={styles.urgentTitle}>
          Urgent Issues
          {risks?.length > 0 && (
            <span className={styles.urgentCount}>{risks.length}</span>
          )}
        </div>
        {risksLoading ? (
          <div className={styles.loading}>
            <LoadingSpinner size="small" />
          </div>
        ) : risksError ? (
          renderRetryError(risksError, onRetryRisks)
        ) : !risks?.length ? (
          <div className={styles.empty}>No urgent issues</div>
        ) : (
          <div className={styles.attentionList}>
            {risks.map((risk) => (
              <Link
                key={`${risk.type}-${risk.orderId}`}
                to={risk.link}
                className={`${styles.attentionItem} ${risk.severity === "red" ? styles.attentionRed : styles.attentionOrange}`}
              >
                <span
                  className={`${styles.severityDot} ${risk.severity === "red" ? styles.dotRed : styles.dotOrange}`}
                />
                <span className={styles.attentionLabel}>
                  {RISK_LABELS[risk.type] || formatValue(risk.type)}
                </span>
                <span className={styles.attentionDetail}>
                  {risk.orderNumber}
                  {risk.stage ? ` \u00B7 ${formatValue(risk.stage)} stage` : ""}
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>}

      {/* Filters */}
      <div className={styles.filtersRow}>
        {selectFilters.map(({ key, placeholder, options: opts }) => (
          <select
            key={key}
            value={filters[key]}
            onChange={handleFilter(key)}
            className={styles.filterSelect}
          >
            <option value="">{placeholder}</option>
            {opts.map((opt) => {
              const value = opt.value ?? opt;
              const label = opt.label ?? formatValue(opt);
              return (
                <option key={value} value={value}>
                  {label}
                </option>
              );
            })}
          </select>
        ))}
        <input type="date" value={filters.from} onChange={handleFilter("from")} className={styles.filterSelect} aria-label="From date" />
        <input type="date" value={filters.to} onChange={handleFilter("to")} className={styles.filterSelect} aria-label="To date" />
      </div>

      {loading ? (
        <div className={styles.loading}>
          <LoadingSpinner />
        </div>
      ) : error ? (
        renderRetryError(error, loadTeam)
      ) : (
        <>
          {/* Global KPIs */}
          <div className={styles.teamKpiGrid}>
            {kpis.map((kpi) => (
              <div key={kpi.label} className={styles.kpiCard}>
                <span className={`${styles.kpiValue} ${kpi.alert && kpi.value > 0 ? styles.kpiAlert : ""}`}>
                  {kpi.value}
                </span>
                <span className={styles.kpiLabel}>{kpi.label}</span>
              </div>
            ))}
          </div>

          {/* Agent Performance */}
          <div className={styles.section}>
            <h3 className={styles.subsectionTitle}>Agent Performance</h3>
            <div className={styles.tableCard}>
              {sortedUsers.length === 0 ? (
                <div className={styles.empty}>No team activity in this window</div>
              ) : (
                <table className={`${styles.table} ${styles.teamTable}`}>
                  <thead>
                    <tr>
                      <th>Agent</th>
                      <th>Role</th>
                      {METRIC_COLUMNS.map((col) => (
                        <th
                          key={col.key}
                          className={styles.sortableTh}
                          onClick={() => handleSort(col.key)}
                        >
                          {col.label}
                          {sortArrow(col.key)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {sortedUsers.map((user) => (
                      <tr
                        key={user.userId}
                        className={styles.rowClickable}
                        onClick={() => openUserTickets(user.userId)}
                      >
                        <td className={styles.ticketNumber}>{user.displayName}</td>
                        <td>{user.role || "\u2014"}</td>
                        {METRIC_COLUMNS.map((col) => (
                          <td key={col.key} className={styles.dateCell}>
                            {formatCell(user, col)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          {/* Aging buckets */}
          {overview?.agingBuckets && (
            <div className={styles.section}>
              <h3 className={styles.subsectionTitle}>Unresolved Ticket Aging</h3>
              <div className={styles.agingGrid}>
                {Object.entries(overview.agingBuckets).map(([bucket, count]) => (
                  <div key={bucket} className={styles.kpiCard}>
                    <span className={`${styles.kpiValue} ${bucket === "24h+" && count > 0 ? styles.kpiAlert : ""}`}>
                      {count}
                    </span>
                    <span className={styles.kpiLabel}>{bucket}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Workload distribution */}
          {sortedUsers.length > 0 && (
            <div className={styles.section}>
              <h3 className={styles.subsectionTitle}>Workload Distribution</h3>
              <div className={styles.workloadCard}>
                {sortedUsers.map((user) => (
                  <div key={user.userId} className={styles.workloadRow}>
                    <span className={styles.workloadName}>{user.displayName}</span>
                    <div className={styles.workloadBar}>
                      <div
                        className={styles.workloadFill}
                        style={{ width: `${((user.activeWorkload || 0) / maxWorkload) * 100}%` }}
                      />
                    </div>
                    <span className={styles.workloadValue}>{user.activeWorkload || 0}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TasksTeamView;
